'use client';

import { Button, Flex, Tabs, TabsValueChangeDetails, Table, Text } from '@chakra-ui/react';
import { memo, useCallback, useState } from 'react';
import { useRouter } from 'next/navigation';
import { LuPlus, LuSettings } from 'react-icons/lu';

import InvoiceStatusBadge from '@features/finance/InvoiceStatusBadge';
import { formatDate, formatMoney, invoiceBalanceDue, invoiceTotal } from '@features/finance/invoiceMeta';
import { allCustomers, allSalesInvoices, SalesInvoice } from 'helpers/fakeData';
import { useHref } from 'hooks/useHref';

type ActiveTab = 'all' | 'draft' | 'outstanding' | 'paid';

const tabs: Array<{ label: string; value: ActiveTab }> = [
  { label: 'All', value: 'all' },
  { label: 'Drafts', value: 'draft' },
  { label: 'Outstanding', value: 'outstanding' },
  { label: 'Paid', value: 'paid' },
];

const matchesTab = (invoice: SalesInvoice, tab: ActiveTab): boolean => {
  switch (tab) {
    case 'draft':
      return invoice.status === 'draft';
    case 'outstanding':
      return invoice.status === 'sent' || invoice.status === 'partially_paid';
    case 'paid':
      return invoice.status === 'paid';
    case 'all':
    default:
      return true;
  }
};

const customerName = (customerId: string): string => {
  return allCustomers.find((customer) => customer.id === customerId)?.name ?? '—';
};

const InvoicesPage = () => {
  const router = useRouter();
  const { salesInvoiceHref, invoiceBuilderHref, invoiceSetupHref } = useHref();
  const [activeTab, setActiveTab] = useState<ActiveTab>('all');

  const handleTabChange = useCallback((details: TabsValueChangeDetails) => {
    setActiveTab(details.value as ActiveTab);
  }, []);
  const onNew = useCallback(() => {
    router.push(invoiceBuilderHref());
  }, [router, invoiceBuilderHref]);
  const onSetup = useCallback(() => {
    router.push(invoiceSetupHref());
  }, [router, invoiceSetupHref]);
  const onRowClick = useCallback(
    (id: string) => () => {
      router.push(salesInvoiceHref(id));
    },
    [router, salesInvoiceHref],
  );

  return (
    <Flex flexDirection="column" gap={4} p={4}>
      <Flex justifyContent="space-between" alignItems="center" gap={2}>
        <Flex direction="column" gap={0.5}>
          <Text fontSize="lg" fontWeight="bold">
            Invoices
          </Text>
          <Text fontSize="sm" color="text.secondary">
            Bill customers and collect through our payment infrastructure.
          </Text>
        </Flex>
        <Flex gap={2} alignItems="center">
          <Button variant="ghost" size="2xs" borderRadius="md" color="text.secondary" onClick={onSetup}>
            <LuSettings size={14} />
            Setup
          </Button>
          <Button size="2xs" borderRadius="md" onClick={onNew}>
            <LuPlus size={14} />
            New invoice
          </Button>
        </Flex>
      </Flex>
      <Tabs.Root
        value={activeTab}
        variant="line"
        size="md"
        onValueChange={handleTabChange}
        display="flex"
        flexDirection="column"
        gap={4}
      >
        <Tabs.List borderColor="border.light" alignItems="center">
          {tabs.map((tab) => (
            <Tabs.Trigger key={tab.value} value={tab.value}>
              <Text fontSize="sm" fontWeight="medium">
                {tab.label}
              </Text>
            </Tabs.Trigger>
          ))}
        </Tabs.List>
        {tabs.map((tab) => {
          const rows = allSalesInvoices.filter((invoice) => matchesTab(invoice, tab.value));
          return (
            <Tabs.Content key={tab.value} value={tab.value} p={0}>
              <Flex direction="column" bg="surface.muted" borderRadius="xl" px={2} pb={2}>
                <Table.Root>
                  <Table.Header fontSize="sm" fontWeight="medium">
                    <Table.Row bg="transparent">
                      <Table.ColumnHeader border="none" color="text.secondary" fontWeight="medium" p={4}>
                        Invoice
                      </Table.ColumnHeader>
                      <Table.ColumnHeader border="none" color="text.secondary" fontWeight="medium" p={4}>
                        Customer
                      </Table.ColumnHeader>
                      <Table.ColumnHeader border="none" color="text.secondary" fontWeight="medium" p={4} hideBelow="md">
                        Due
                      </Table.ColumnHeader>
                      <Table.ColumnHeader border="none" color="text.secondary" fontWeight="medium" p={4} textAlign="end">
                        Total
                      </Table.ColumnHeader>
                      <Table.ColumnHeader border="none" color="text.secondary" fontWeight="medium" p={4} textAlign="end">
                        Balance
                      </Table.ColumnHeader>
                      <Table.ColumnHeader border="none" color="text.secondary" fontWeight="medium" p={4} textAlign="end">
                        Status
                      </Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {rows.map((invoice, index) => (
                      <Table.Row key={invoice.id} fontSize="sm" cursor="pointer" onClick={onRowClick(invoice.id)}>
                        <Table.Cell
                          border="none"
                          p={4}
                          fontWeight="medium"
                          borderTopLeftRadius={index === 0 ? 'xl' : 'none'}
                          borderBottomLeftRadius={index === rows.length - 1 ? 'xl' : 'none'}
                        >
                          {invoice.number}
                        </Table.Cell>
                        <Table.Cell border="none" p={4}>
                          {customerName(invoice.customerId)}
                        </Table.Cell>
                        <Table.Cell border="none" p={4} hideBelow="md" color="text.secondary">
                          {formatDate(invoice.dueDate)}
                        </Table.Cell>
                        <Table.Cell border="none" p={4} textAlign="end">
                          GHS {formatMoney(invoiceTotal(invoice))}
                        </Table.Cell>
                        <Table.Cell border="none" p={4} textAlign="end" fontWeight="medium">
                          GHS {formatMoney(invoiceBalanceDue(invoice))}
                        </Table.Cell>
                        <Table.Cell
                          border="none"
                          p={4}
                          borderTopRightRadius={index === 0 ? 'xl' : 'none'}
                          borderBottomRightRadius={index === rows.length - 1 ? 'xl' : 'none'}
                        >
                          <Flex justifyContent="flex-end">
                            <InvoiceStatusBadge status={invoice.status} />
                          </Flex>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Flex>
            </Tabs.Content>
          );
        })}
      </Tabs.Root>
    </Flex>
  );
};

export default memo(InvoicesPage);
